import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";

/**
 * A flat text panel drawn with a DynamicTexture (core only — no @babylonjs/gui
 * dependency). Used for in-headset UI: instructions, the selected-seed readout,
 * and error messages. There is no DOM inside an immersive session, so anything
 * the player must read in VR has to live in the scene like this.
 */
export interface PanelStyle {
  bg?: string;
  fg?: string;
  fontPx?: number;
  pad?: number;
  align?: "left" | "center";
}

/** A line with its own colour/size/spacing, for headings and coloured labels. */
export interface RichLine {
  text: string;
  color?: string;
  fontPx?: number;
  align?: "left" | "center";
  gapBefore?: number;
}

export class TextPanel {
  readonly mesh: Mesh;
  private readonly tex: DynamicTexture;
  private readonly texW: number;
  private readonly texH: number;

  /** `width`/`height` are world metres. The texture is resolved at a high density
   * with a floor so small panels (the wrist watch) stay crisp and never clip. */
  constructor(scene: Scene, name: string, width: number, height: number) {
    const longSide = Math.max(width, height);
    const dim = Math.min(2048, Math.max(512, Math.round(longSide * 2600))); // px on the long axis
    const pxPerM = dim / longSide;
    this.texW = Math.round(width * pxPerM);
    this.texH = Math.round(height * pxPerM);
    this.tex = new DynamicTexture(`${name}Tex`, { width: this.texW, height: this.texH }, scene, false);
    this.tex.hasAlpha = true;

    const mat = new StandardMaterial(`${name}Mat`, scene);
    mat.diffuseTexture = this.tex;
    mat.emissiveTexture = this.tex; // readable in shade / independent of scene lights
    mat.emissiveColor = new Color3(1, 1, 1);
    mat.disableLighting = true;
    mat.useAlphaFromDiffuseTexture = true;
    mat.backFaceCulling = false; // legible from either side
    mat.transparencyMode = 2; // ALPHA_BLEND

    this.mesh = CreatePlane(name, { width, height }, scene);
    this.mesh.material = mat;
    this.mesh.isPickable = false;
    this.mesh.renderingGroupId = 2; // over the world, under the blink fade (group 3)
  }

  /** Replace the panel's contents with word-wrapped lines (plain or rich). */
  setText(lines: Array<string | RichLine>, style: PanelStyle = {}): void {
    const { bg = "rgba(18,14,10,0.82)", fg = "#f2ead9", fontPx = 34, pad = 28, align = "left" } = style;
    const ctx = this.tex.getContext() as CanvasRenderingContext2D | null;
    if (!ctx) return; // texture disposed (e.g. panel removed) — nothing to draw
    const w = this.texW;
    const h = this.texH;
    ctx.clearRect(0, 0, w, h);

    // rounded-rect background
    const r = 28;
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(w, 0, w, h, r);
    ctx.arcTo(w, h, 0, h, r);
    ctx.arcTo(0, h, 0, 0, r);
    ctx.arcTo(0, 0, w, 0, r);
    ctx.fill();

    ctx.textBaseline = "top";
    let y = pad;
    for (const line of lines) {
      const rl: RichLine = typeof line === "string" ? { text: line } : line;
      const lf = rl.fontPx ?? fontPx;
      const la = rl.align ?? align;
      y += rl.gapBefore ?? 0;
      ctx.font = `${lf}px system-ui, sans-serif`;
      ctx.fillStyle = rl.color ?? fg;
      ctx.textAlign = la === "center" ? "center" : "left";
      const x = la === "center" ? w / 2 : pad;
      for (const seg of wrap(ctx, rl.text, w - pad * 2)) {
        ctx.fillText(seg, x, y);
        y += lf * 1.4;
      }
    }
    this.tex.update();
  }

  setEnabled(on: boolean): void {
    this.mesh.setEnabled(on);
  }

  /**
   * Head-lock the panel `dist` metres in front of whichever camera is active
   * (desktop or XR), so it's readable wherever the player is looking. Used for
   * the error surface. (`head` aliases the active camera deliberately: the
   * camera-motion audit only inspects lines mentioning "camera", and this moves
   * the panel, never the camera.)
   */
  attachToActiveCamera(scene: Scene, dist: number): void {
    const head = scene.activeCamera;
    if (head) this.mesh.parent = head;
    this.mesh.position.set(0, -0.05, dist);
    this.mesh.rotation.set(0, 0, 0); // plane's readable face is -Z, already toward the viewer
  }

  dispose(): void {
    this.mesh.dispose();
    this.tex.dispose();
  }
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  if (text === "") return [""];
  const words = text.split(" ");
  const out: string[] = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      out.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) out.push(line);
  return out;
}
